<script setup lang="ts">
import { createApp, h, ref, onMounted, onUnmounted, computed, watch } from "vue";
import type { Ref } from "vue";
import pangu from "pangu/browser";
import { PostLoginRefresh } from "@/apis/user.ts";
import { TryAutoCheckin } from "@/apis/checkin.ts";
import { useLoginStore } from "@/stores/login";
import { useUserStore } from "@/stores/user.ts";
import { useWebStyleStore } from "@/stores/webStyle";
import { useSidebarStyleStore } from "@/stores/sidebarStyle";
import { useFooterStyleStore } from "@/stores/footerStyle";
import { GetProblemAttemptStatusByKey, ProblemAttemptStatus } from "@/apis/problem.ts";
import { Button } from "tdesign-vue-next";
import { useBreakpoint } from "@/composables/useBreakpoint";
import { useRoute, useRouter } from "vue-router";
import type { RouteLocationNormalized } from "vue-router";

import HeaderContent from "./components/HeaderContent.vue";
import SidebarContent from "./components/SidebarContent.vue";
import BreadcrumbContent from "./components/BreadcrumbContent.vue";
import FooterContent from "./components/FooterContent.vue";
import ForceHiddenSidebarButton from "./components/ForceHiddenSidebarButton.vue";
import MobileNavDrawer from "./components/layout/MobileNavDrawer.vue";
import AiCoachPanel from "./components/ai/AiCoachPanel.vue";
import View403 from "@/views/View403.vue";
import RouteLoadingBar from "./components/RouteLoadingBar.vue";
import RouteLoadingSkeleton from "./components/RouteLoadingSkeleton.vue";

import { debounce, useCurrentInstance } from "@/util/";
import { setSeoTitle } from "@/util/seo";
import { ShowEnhancedAwardTips, ShowErrorTips, ShowTextTipsSuccess } from "@/util/tips.ts";
import { enableClickPositionTracking, disableClickPositionTracking } from "@/util/click-position";
import { createFireworks as CreateFireworks } from "./util/fireworks";

const { globalProperties } = useCurrentInstance();

const loginStore = useLoginStore();
const userStore = useUserStore();
const webStyleStore = useWebStyleStore();
const sidebarStyleStore = useSidebarStyleStore();
const footerStyleStore = useFooterStyleStore();
const route = useRoute();
const router = useRouter();
const { isMobile } = useBreakpoint();

// 路由懒加载：切换路由时显示顶部加载进度条
const routeLoading = ref(false);
let loadingTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * 路由「页面标识」：默认用 fullPath（同页参数变化即重建，页面据此重新取数）。
 * 声明了 meta.viewKey 的界面（同一页面挂在多条路由上，如 AI 教练主界面的
 * /ai/conversation 与 /ai/conversation/:id）改用 viewKey——它们本来就是同一个页面，
 * 切换地址不该重建组件、也不该闪顶部加载条（选中态写回地址栏就靠它）。
 */
const routeViewKey = (r: RouteLocationNormalized): string => (r.meta.viewKey as string | undefined) ?? r.fullPath;

router.beforeEach((to, from, next) => {
  // 仅在真正切换页面时显示加载条，同页参数变化不显示
  if (routeViewKey(to) !== routeViewKey(from)) {
    routeLoading.value = true;
    // 安全超时：最长 5 秒自动隐藏，防止异常卡住
    if (loadingTimer) clearTimeout(loadingTimer);
    loadingTimer = setTimeout(() => {
      routeLoading.value = false;
    }, 5000);
  }
  next();
});

router.afterEach(() => {
  if (loadingTimer) clearTimeout(loadingTimer);
  // 短暂延迟，让新页面有机会挂载
  setTimeout(() => {
    routeLoading.value = false;
  }, 200);
});

// 移动端导航抽屉显隐
const mobileNavVisible = ref(false);
const handleOpenMobileNav = () => {
  mobileNavVisible.value = true;
};

loginStore.$patch({
  Loaded: false,
});

const showSidebar = ref(false);
const realShowSidebar = ref(false);
const showFooter = ref(true);
const realShowFooter = ref(true);

const titleBase = "DidaOJ";

// <title> 一律经由 util/seo.ts 写入：它还负责同步 og:title / twitter:title。
// 直接写 document.title 会绕过这些同步，导致社交卡片标题与页面标题不一致。
if (webStyleStore.title && webStyleStore.title.length > 0) {
  setSeoTitle(webStyleStore.title + " - " + titleBase);
} else {
  setSeoTitle(titleBase);
}

webStyleStore.$subscribe((_, state) => {
  if (state.title && state.title.length > 0) {
    setSeoTitle(state.title + " - " + titleBase);
  } else {
    setSeoTitle(titleBase);
  }
});

sidebarStyleStore.$subscribe((_, state) => {
  showSidebar.value = state.showSidebar;
  realShowSidebar.value = showSidebar.value && !state.forceHiddenSidebar;
});

footerStyleStore.$subscribe((_, state) => {
  showFooter.value = state.showFooter;
  realShowFooter.value = showFooter.value && !state.forceHiddenFooter;
});

const hasNotAuth = computed(() => {
  const auths = route.meta.auths as string[] | undefined;
  return auths ? !userStore.hasAllAuths(auths) : false;
});

function handleError() {
  globalProperties.$message.warning({
    duration: 3000,
    content: "登陆状态失效",
  });
  loginStore.$patch({
    Loaded: true,
  });
  userStore.clear();
}

const processProblemTags = async () => {
  const containers = document.querySelectorAll("oj-problem");
  if (containers.length === 0) {
    return;
  }
  const buttonThemes = {} as { [key: string]: Ref<string>[] };
  containers.forEach((el) => {
    const key = el.getAttribute("key") as string;
    const mountNode = document.createElement("span");
    mountNode.setAttribute("style", "padding:5px");
    el.replaceWith(mountNode);
    const theme = ref("default");
    if (!buttonThemes[key]) {
      buttonThemes[key] = [];
    }
    buttonThemes[key].push(theme);
    let problemId = key;
    if (key && !key.includes("-")) {
      problemId = "DidaOJ-" + key;
    }
    const app = createApp({
      render: () =>
        h(Button, {
          innerHTML: problemId,
          onClick: () => {
            window.open(`/problem/${key}`, "_blank");
          },
          size: "small",
          variant: "outline",
          theme: theme.value as "default" | "primary" | "success" | "warning" | "danger" | undefined,
        }),
    });
    app.mount(mountNode);
  });
  const problemKeys = [];
  for (const key in buttonThemes) {
    problemKeys.push(key);
  }
  const res = await GetProblemAttemptStatusByKey(problemKeys);
  if (res.code === 0) {
    for (const id in buttonThemes) {
      const themes = buttonThemes[id];
      if (!themes) {
        continue;
      }
      let theme = "primary";
      const status = res.data[id];
      if (status) {
        switch (status) {
          case ProblemAttemptStatus.Accept:
            theme = "success";
            break;
          case ProblemAttemptStatus.Attempt:
            theme = "warning";
            break;
        }
      }
      for (const t of themes) {
        t.value = theme;
      }
    }
  }
};

const CHECKIN_INTERVAL_MS = 5 * 60 * 1000;
let checkinIntervalId: ReturnType<typeof setInterval> | null = null;

const tryAutoCheckin = async () => {
  if (!userStore.isLogin()) return;
  const userId = userStore.getUserId;
  try {
    const result = await TryAutoCheckin(userId);
    if (result && result.just_checked_in && result.award) {
      ShowTextTipsSuccess(globalProperties, "签到成功！");
      CreateFireworks();
      ShowEnhancedAwardTips(globalProperties, result.award, 4000);
    }
  } catch (e) {
    console.error("auto checkin failed:", e);
  }
};

const startCheckinInterval = () => {
  stopCheckinInterval();
  checkinIntervalId = setInterval(() => {
    tryAutoCheckin();
  }, CHECKIN_INTERVAL_MS);
};

const stopCheckinInterval = () => {
  if (checkinIntervalId !== null) {
    clearInterval(checkinIntervalId);
    checkinIntervalId = null;
  }
};

onMounted(() => {
  enableClickPositionTracking();

  document.addEventListener("DOMContentLoaded", () => {
    const observer = new MutationObserver(() => {
      debounce(() => {
        processProblemTags();
        // 仅对 md-preview 渲染的内容执行盘古之白处理，避免影响全页其他文本
        document.querySelectorAll(".md-editor-preview").forEach((node) => {
          pangu.spacingNode(node);
        });
      }, 1000)();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    // 首次处理已存在的 md-preview 内容
    document.querySelectorAll(".md-editor-preview").forEach((node) => {
      pangu.spacingNode(node);
    });
  });

  if (userStore.isLogin()) {
    PostLoginRefresh()
      .then((res) => {
        if (res.code == 0) {
          userStore.loadResponse(res.data);
          loginStore.$patch({
            Loaded: true,
          });

          tryAutoCheckin();
          startCheckinInterval();
        } else {
          ShowErrorTips(globalProperties, res.code);
          loginStore.$patch({
            Loaded: true,
          });
          userStore.clear();
        }
      })
      .catch((err) => {
        console.log(err);
        handleError();
      });
  } else {
    loginStore.$patch({
      Loaded: true,
    });
  }
});

watch(() => route.name, (newName, oldName) => {
  if (newName && newName !== oldName && userStore.isLogin() && loginStore.isLoaded) {
    tryAutoCheckin();
    if (!checkinIntervalId) {
      startCheckinInterval();
    }
  }
});

watch(() => userStore.isLogin(), (isLoggedIn) => {
  if (!isLoggedIn) {
    stopCheckinInterval();
  }
});

onUnmounted(() => {
  disableClickPositionTracking();
  stopCheckinInterval();
});
</script>

<template>
  <t-layout class="sh-app-layout">
    <t-header class="sh-header">
      <HeaderContent @open-mobile-nav="handleOpenMobileNav" />
    </t-header>
    <t-layout>
      <!-- 桌面端固定侧边栏（移动端不渲染，由 MobileNavDrawer 承载） -->
      <Transition v-if="!isMobile" name="slide">
        <t-aside v-if="realShowSidebar" class="sh-sidebar">
          <SidebarContent />
        </t-aside>
      </Transition>
      <t-layout :class="['sh-main-layout', { expanded: !realShowSidebar || isMobile }]">
        <BreadcrumbContent />
        <t-content class="sh-main-content">
          <!-- 路由懒加载：顶部进度条 -->
          <RouteLoadingBar v-if="routeLoading" />
          <!-- 应用初始化中：登录状态尚未确认，显示骨架屏。
               注意：t-skeleton 没有 rows 属性，写 :rows="N" 只会被当成无效 HTML 属性渲染，
               实际仍只有一行默认骨架（页面因此被压得很扁）。必须用 row-col 显式描述每一行。 -->
          <div v-if="!loginStore.isLoaded" class="app-initial-loading">
            <t-skeleton
              :row-col="[
                { width: '40%', height: '28px' },
                [{ width: '100%', height: '160px' }],
                [{ width: '100%', height: '24px' }, { width: '100%', height: '24px' }, { width: '60%', height: '24px' }],
              ]"
              animation="gradient"
            />
          </div>
          <template v-else>
            <View403 v-if="hasNotAuth" />
            <RouterView v-else v-slot="{ Component, route: r }">
              <Suspense>
                <component :is="Component" :key="routeViewKey(r)" />
                <template #fallback>
                  <RouteLoadingSkeleton />
                </template>
              </Suspense>
            </RouterView>
          </template>
        </t-content>
        <FooterContent v-if="realShowFooter" />
      </t-layout>
    </t-layout>
  </t-layout>
  <!-- 桌面端：浮动隐藏侧边栏按钮（仅桌面端 + showSidebar 时显示） -->
  <ForceHiddenSidebarButton v-if="showSidebar && !isMobile" />
  <!-- 移动端：导航抽屉（顶部菜单 + 侧边栏 + 用户区合并） -->
  <MobileNavDrawer v-if="isMobile" v-model:visible="mobileNavVisible" />
  <!-- AI 教练浮窗：全局唯一实例（挂在此处，路由切换不销毁，会话状态存 pinia） -->
  <AiCoachPanel />
  <t-back-top container="body" :offset="['24px', '80px']" size="small"></t-back-top>
</template>

<style scoped>
.sh-app-layout {
  /* 移除 min-width: 1440px，允许窄屏自适应 */
  /* 撑满视口高度，配合下面的 flex 让页脚贴底：
     否则内容较短的页面（含首屏骨架屏）页脚会悬浮在视口中间，下方留一大片空白 */
  min-height: 100vh;
}

.sh-header {
  padding: 0px;
  position: sticky;
  width: 100%;
  top: 0px;
  z-index: 1000;
  border-bottom: 1px solid #dcdee2;
  /* 固定高度，不参与拉伸 */
  flex: none;
}

.sh-sidebar {
  position: fixed;
  left: 0px;
  top: calc(56px + 1px);
  overflow: auto;
  z-index: 1000;
}

.sh-sidebar:after {
  content: "";
  display: block;
  width: 1px;
  height: 100%;
  background: #dcdee2;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
}

.sh-main-layout {
  transition: margin-left 0.3s ease;
  margin-left: 232px;
  background: #fff;
  /* 占满 header 之外的全部剩余高度，页脚因此被顶到底部 */
  flex: 1 1 auto;
}

.sh-main-layout.expanded {
  margin-left: 0;
}

.slide-enter-active {
  transition:
    transform 0.5s ease,
    opacity 0.5s ease;
}

.slide-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

/* 应用初始化加载：登录状态确认前的占位。
   真实原因是骨架屏高度太低：登录态未确认期间页面只有寥寥几十像素高，
   页脚被顶到视口中间、下方一片空白，整页看起来非常扁。
   这里让占位撑到接近一屏，并拉开行距。 */
.app-initial-loading {
  width: 100%;
  padding: 24px;
}

/* 撑起首屏高度：骨架行本身很矮，若不加下限，登录态确认前页面只有顶部一条，
   配合粘性页脚会把页脚顶到视口中间，视觉上非常扁 */
.app-initial-loading :deep(.t-skeleton) {
  min-height: 60vh;
}

.app-initial-loading :deep(.t-skeleton__row) {
  margin-bottom: 20px;
}

.app-initial-loading :deep(.t-skeleton__row:last-child) {
  margin-bottom: 0;
}

/* ============ 移动端布局适配 ============ */
@media (max-width: 800px) {
  /* 移动端主内容区不预留侧边栏空间 */
  .sh-main-layout {
    margin-left: 0 !important;
  }

  /* 让内容区有合适的内边距 */
  .sh-main-content {
    padding: 8px;
  }
}
</style>
